import type { DomainEvent, DomainEventType, EventOf } from './types';

type Handler<T extends DomainEventType> = (event: EventOf<T>) => void;
type AnyHandler = (event: DomainEvent) => void;

/**
 * Typed, synchronous event bus (content-schemas §12.1). Presenters (SFX, sparkles, toasts) listen;
 * only the dispatcher emits. No history is kept.
 */
export class EventBus {
  private readonly handlers = new Map<DomainEventType, Set<AnyHandler>>();
  private readonly any = new Set<AnyHandler>();

  on<T extends DomainEventType>(type: T, handler: Handler<T>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const h = handler as AnyHandler;
    set.add(h);
    return () => {
      set.delete(h);
    };
  }

  onAny(handler: AnyHandler): () => void {
    this.any.add(handler);
    return () => {
      this.any.delete(handler);
    };
  }

  emitAll(events: readonly DomainEvent[]): void {
    for (const event of events) {
      for (const handler of this.handlers.get(event.type) ?? []) safeCall(handler, event);
      for (const handler of this.any) safeCall(handler, event);
    }
  }
}

/** A broken presenter must never break the game loop or lose state. */
function safeCall(handler: AnyHandler, event: DomainEvent): void {
  try {
    handler(event);
  } catch (error) {
    console.error('[bus] handler failed for', event.type, error);
  }
}
